import { useEffect, useState, type ReactNode } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  TransactionSort,
  TransactionSource,
  TransactionType,
} from "@boo/shared";
import { useAppStore } from "../../store/useAppStore";
import { useAccountStore } from "@/features/accounts/store/useAccountStore";
import AccountAvatar from "@/features/accounts/components/AccountAvatar";
import { formatSource } from "../../lib/formatSource";
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  EXPENSE_CATEGORIES,
  SORT_LABELS,
  hasActiveFilter,
} from "../../types";

interface ChipOption<T extends string> {
  value: T;
  label: string;
  color?: string;
  icon?: ReactNode;
}

interface FilterChipProps<T extends string> {
  label: string;
  value: T | null;
  options: ChipOption<T>[];
  onChange: (value: T | null) => void;
  // Label for the "no filter" option; omit when a value is always required.
  allLabel?: string;
  disabled?: boolean;
}

function FilterChip<T extends string>({
  label,
  value,
  options,
  onChange,
  allLabel,
  disabled = false,
}: FilterChipProps<T>) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  const isActive = allLabel !== undefined && value !== null;

  const select = (next: T | null) => {
    onChange(next);
    setOpen(false);
  };

  const optionClass = (isSelected: boolean) =>
    `w-full flex items-center gap-2.5 text-[13px] font-medium px-2.5 py-1.5 rounded-lg text-left transition-colors text-foreground ${
      isSelected
        ? "bg-[var(--color-primary-bg)] hover:bg-[var(--color-primary-bg)]"
        : "bg-transparent hover:bg-[var(--surface-muted)]"
    }`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-[10px] transition-colors cursor-pointer border border-border hover:bg-[var(--surface-muted)] disabled:opacity-40 disabled:cursor-not-allowed ${
            isActive
              ? "bg-[var(--color-primary-bg)] text-foreground"
              : "bg-transparent text-[var(--text-secondary)]"
          }`}
        >
          {selected?.label ?? label}
          <ChevronDown
            className={`w-3 h-3 opacity-60 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-1.5 rounded-xl w-auto min-w-[168px] bg-card border-[0.5px] border-border shadow-popover"
        align="start"
        role="listbox"
        aria-label={label}
      >
        {allLabel !== undefined && (
          <button
            role="option"
            aria-selected={value === null}
            onClick={() => select(null)}
            className={optionClass(value === null)}
          >
            {allLabel}
            {value === null && (
              <Check className="w-3.5 h-3.5 ml-auto text-primary" />
            )}
          </button>
        )}
        {options.map((opt) => (
          <button
            key={opt.value}
            role="option"
            aria-selected={opt.value === value}
            onClick={() => select(opt.value)}
            className={optionClass(opt.value === value)}
          >
            {opt.color && (
              <span
                className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0"
                style={{ background: opt.color }}
              />
            )}
            {opt.icon}
            {opt.label}
            {opt.value === value && (
              <Check className="w-3.5 h-3.5 ml-auto text-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

const CATEGORY_OPTIONS = EXPENSE_CATEGORIES.map((cat) => ({
  value: cat,
  label: CATEGORY_LABELS[cat],
  color: CATEGORY_COLORS[cat],
}));

const TYPE_OPTIONS = [
  { value: TransactionType.EXPENSE, label: "Expense" },
  { value: TransactionType.INCOME, label: "Income" },
  { value: TransactionType.TRANSFER, label: "Transfer" },
  { value: TransactionType.REIMBURSEMENT, label: "Payback" },
];

const SOURCE_OPTIONS = Object.values(TransactionSource).map((source) => ({
  value: source,
  label: formatSource(source),
}));

const SORT_OPTIONS = Object.values(TransactionSort).map((sort) => ({
  value: sort,
  label: SORT_LABELS[sort],
}));

const SEARCH_DEBOUNCE_MS = 300;

export default function TransactionsToolbar() {
  const sort = useAppStore((s) => s.transactionSort);
  const filters = useAppStore((s) => s.transactionFilters);
  const setSort = useAppStore((s) => s.setTransactionSort);
  const setFilters = useAppStore((s) => s.setTransactionFilters);
  const clearFilters = useAppStore((s) => s.clearTransactionFilters);
  const accounts = useAccountStore((s) => s.accounts);
  const accountOptions = accounts.map((account) => ({
    value: account.id,
    label: account.name,
    icon: (
      <AccountAvatar
        size="sm"
        type={account.type}
        bankCode={account.bankCode}
      />
    ),
  }));

  // Local input value, debounced into the store. Re-synced when the store's
  // title changes from elsewhere (e.g. "Clear").
  const [query, setQuery] = useState(filters.title);
  const [syncedTitle, setSyncedTitle] = useState(filters.title);
  if (filters.title !== syncedTitle) {
    setSyncedTitle(filters.title);
    setQuery(filters.title);
  }

  useEffect(() => {
    if (query === filters.title) return;
    const timer = setTimeout(
      () => setFilters({ title: query }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [query, filters.title, setFilters]);

  const isNonExpenseOnly =
    filters.transactionType !== null &&
    filters.transactionType !== TransactionType.EXPENSE;
  const canClear =
    hasActiveFilter(filters) || sort !== TransactionSort.DateDesc;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <div className="relative w-full sm:w-56">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title"
          aria-label="Search transactions"
          className="w-full text-[12px] text-foreground bg-[var(--surface-muted)] border border-border rounded-[10px] pl-8 pr-3 py-1.5 outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
        />
      </div>
      {/* Mobile: one horizontally scrollable row. From sm the wrapper
          dissolves and chips wrap with the search box as before. */}
      <div className="flex items-center gap-2 w-[calc(100%+2.5rem)] -mx-5 px-5 overflow-x-auto scrollbar-none sm:contents">
        <FilterChip
          label="Category"
          allLabel="All categories"
          value={filters.category}
          options={CATEGORY_OPTIONS}
          onChange={(category) => setFilters({ category })}
          disabled={isNonExpenseOnly}
        />
        <FilterChip
          label="Type"
          allLabel="All types"
          value={filters.transactionType}
          options={TYPE_OPTIONS}
          onChange={(transactionType) => setFilters({ transactionType })}
        />
        <FilterChip
          label="Source"
          allLabel="All sources"
          value={filters.source}
          options={SOURCE_OPTIONS}
          onChange={(source) => setFilters({ source })}
        />
        {accounts.length > 0 && (
          <FilterChip
            label="Account"
            allLabel="All accounts"
            value={filters.accountId}
            options={accountOptions}
            onChange={(accountId) =>
              setFilters({ accountId, unassigned: false })
            }
          />
        )}
        <button
          type="button"
          aria-pressed={filters.unassigned}
          onClick={() =>
            setFilters({ unassigned: !filters.unassigned, accountId: null })
          }
          className={`inline-flex shrink-0 whitespace-nowrap items-center text-[12px] font-medium px-3 py-1.5 rounded-[10px] transition-colors cursor-pointer border border-border hover:bg-[var(--surface-muted)] ${
            filters.unassigned
              ? "bg-[var(--color-primary-bg)] text-foreground"
              : "bg-transparent text-[var(--text-secondary)]"
          }`}
        >
          Unassigned
        </button>
        <FilterChip
          label="Sort"
          value={sort}
          options={SORT_OPTIONS}
          onChange={(next) => next && setSort(next)}
        />
        {canClear && (
          <button
            type="button"
            onClick={() => clearFilters()}
            className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium px-2 py-1.5 rounded-[10px] transition-colors cursor-pointer bg-transparent text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
